/**
 * School District Grade Query System
 *
 * 基于索引的学区-年级查询系统
 * 参考文档: docs/SCHOOL_DISTRICT_GRADE_MAPPING_SPEC.md
 *
 * 数据结构：
 * - default_grade: 默认年级（单个字符串，如 "Grade 3"）
 * - public_school_districts: 公立学区列表
 * - school_district_grade_mapping: 特殊映射（补充意外情况）
 *
 * 匹配逻辑：
 * - 如果用户学区在配置中 → 匹配年级
 * - 如果用户学区不在配置中 → 直接匹配年级
 *
 * 年级格式转换：
 * - Profile 存储格式: "3", "4", "5" (纯数字字符串)
 * - Quest 存储格式: "Grade 3", "Grade 5" (格式化字符串)
 * - 使用 normalizeGrade() 进行统一转换
 */

import { Quest, SchoolDistrictGradeMapping } from '@/constant/schema';

/**
 * 将用户的 grade 格式标准化为 'Grade X' 格式
 *
 * @param grade - 用户的 grade，可能是 '3', '4', 'grade3', 'Grade 3' 等格式
 * @returns 标准化后的格式，如 'Grade 3'
 *
 * @example
 * normalizeGrade('3')        // 'Grade 3'
 * normalizeGrade('grade3')   // 'Grade 3'
 * normalizeGrade('Grade 3')  // 'Grade 3'
 */
export function normalizeGrade(grade: string | undefined): string | undefined {
  if (!grade) return undefined;

  // 如果已经是 'Grade X' 格式，直接返回
  if (/^Grade \d+$/.test(grade)) return grade;

  // 如果是 'gradeX' 格式，提取数字
  if (/^grade\d+$/.test(grade)) {
    const num = grade.replace('grade', '');
    return `Grade ${num}`;
  }

  // 如果是纯数字字符串，转换为 'Grade X'
  if (/^\d+$/.test(grade)) {
    return `Grade ${grade}`;
  }

  return grade;
}

export function normalizeSchoolDistrict(userDistrict: string): string {
  if (!userDistrict) return '';
  if (userDistrict.toLowerCase().trim() === 'basis' || userDistrict.toLowerCase().trim() === 'basis independent') return 'BASIS Independent Schools';
  if (userDistrict.toLowerCase().trim() === 'challenger') return 'Challenger Schools';
  if (userDistrict.toLowerCase().trim() === 'basis charter') return 'BASIS Charter Schools';
  return userDistrict;
}

/**
 * 获取下一个年级（返回 normalized 格式）
 *
 * @param grade - 当前年级，可能是 'K', 'grade K', 'Grade K', '3', 'Grade 3' 等格式
 * @returns 下一个年级的 normalized 格式，如果无法解析则返回 undefined
 *
 * @example
 * getNextGrade('K')          // 'Grade 1'
 * getNextGrade('grade K')    // 'Grade 1'
 * getNextGrade('Grade K')    // 'Grade 1'
 * getNextGrade('3')          // 'Grade 4'
 * getNextGrade('Grade 3')    // 'Grade 4'
 * getNextGrade('Grade 12')   // 'Grade 13'
 */
export function getNextGrade(grade: string | undefined): string | undefined {
  if (!grade) return undefined;

  // 先规范化输入
  const normalizedInput = grade.trim();

  // 处理 K (Kindergarten) 的情况
  if (/^(K|grade\s*K|Grade\s*K)$/i.test(normalizedInput)) {
    return 'Grade 1';
  }

  // 提取数字
  const gradeMatch = normalizedInput.match(/\d+/);
  if (!gradeMatch) return undefined;

  const gradeNumber = parseInt(gradeMatch[0]);
  return `Grade ${gradeNumber + 1}`;
}

/**
 * Quest 查询索引
 */
export interface QuestIndexes {
  // 学区 -> 年级 -> Quest ID 集合
  districtGradeIndex: Map<string, Map<string, Set<string>>>;

  // 年级 -> Quest ID 集合
  gradeIndex: Map<string, Set<string>>;

  // Quest ID -> Quest 对象映射
  questMap: Map<string, Quest>;
}

/**
 * 从 Quest 列表构建查询索引
 *
 * @param quests - Quest 列表
 * @returns 查询索引
 *
 * @example
 * const allQuests = await getAllQuests();
 * const indexes = buildQuestIndexes(allQuests);
 */
export function buildQuestIndexes(quests: Quest[]): QuestIndexes {
  const districtGradeIndex = new Map<string, Map<string, Set<string>>>();
  const gradeIndex = new Map<string, Set<string>>();
  const questMap = new Map<string, Quest>();

  quests.forEach(quest => {
    questMap.set(quest.id, quest);

    const defaultGrade = quest.default_grade;
    const publicDistricts = quest.public_school_districts || [];
    const specialMappings = quest.school_district_grade_mapping || [];

    // === 1. 处理 public_school_districts + default_grade ===
    if (defaultGrade && publicDistricts.length > 0) {
      publicDistricts.forEach(district => {
        // 添加到 districtGradeIndex
        if (!districtGradeIndex.has(district)) {
          districtGradeIndex.set(district, new Map());
        }
        const gradeMap = districtGradeIndex.get(district)!;
        if (!gradeMap.has(defaultGrade)) {
          gradeMap.set(defaultGrade, new Set());
        }
        gradeMap.get(defaultGrade)!.add(quest.id);
      });

      // 添加到 gradeIndex
      if (!gradeIndex.has(defaultGrade)) {
        gradeIndex.set(defaultGrade, new Set());
      }
      gradeIndex.get(defaultGrade)!.add(quest.id);
    }

    // === 2. 处理 school_district_grade_mapping（特殊case）===
    specialMappings.forEach(mapping => {
      const district = mapping.school_district;
      const grades = mapping.grades && mapping.grades.length > 0
        ? mapping.grades
        : (defaultGrade ? [defaultGrade] : []);

      grades.forEach(grade => {
        // 添加到 districtGradeIndex
        if (!districtGradeIndex.has(district)) {
          districtGradeIndex.set(district, new Map());
        }
        const gradeMap = districtGradeIndex.get(district)!;
        if (!gradeMap.has(grade)) {
          gradeMap.set(grade, new Set());
        }
        gradeMap.get(grade)!.add(quest.id);

        // 添加到 gradeIndex
        if (!gradeIndex.has(grade)) {
          gradeIndex.set(grade, new Set());
        }
        gradeIndex.get(grade)!.add(quest.id);
      });
    });
  });

  return {
    districtGradeIndex,
    gradeIndex,
    questMap
  };
}

/**
 * 根据学区和年级查询 Quest
 *
 * @param indexes - Quest 索引
 * @param schoolDistrict - 学区名称，'all' 表示不筛选学区
 * @param grade - 年级，'all' 表示不筛选年级
 * @returns Quest 列表
 *
 * @example
 * // 查询 Challenger Schools 的 Grade 3 教材
 * const quests = queryQuestsByDistrictAndGrade(indexes, 'Challenger Schools', 'Grade 3');
 *
 * @example
 * // 查询所有 Grade 3 教材（不限学区）
 * const quests = queryQuestsByDistrictAndGrade(indexes, 'all', 'Grade 3');
 */
export function queryQuestsByDistrictAndGrade(
  indexes: QuestIndexes,
  schoolDistrict: string = 'all',
  grade: string = 'all'
): Quest[] {
  let questIds: Set<string>;

  // === 场景1: 用户选择了学区 ===
  if (schoolDistrict && schoolDistrict !== 'all') {
    const gradeMap = indexes.districtGradeIndex.get(schoolDistrict);

    if (!gradeMap) {
      // 学区不在任何配置中
      return [];
    }

    if (grade && grade !== 'all') {
      // 学区 + 年级都有筛选
      questIds = gradeMap.get(grade) || new Set();
    } else {
      // 只有学区筛选，返回该学区所有年级的 Quest
      questIds = new Set();
      gradeMap.forEach(ids => {
        ids.forEach(id => questIds.add(id));
      });
    }
  }
  // === 场景2: 用户没有选择学区 ===
  else {
    if (grade && grade !== 'all') {
      // 只有年级筛选
      questIds = indexes.gradeIndex.get(grade) || new Set();
    } else {
      // 没有任何筛选，返回所有 Quest
      questIds = new Set(indexes.questMap.keys());
    }
  }

  // 转换为 Quest 对象并返回
  return Array.from(questIds)
    .map(id => indexes.questMap.get(id))
    .filter((q): q is Quest => q !== undefined);
}

/**
 * 获取所有可用的学区列表
 *
 * @param indexes - Quest 索引
 * @returns 学区列表（按字母顺序排序）
 *
 * @example
 * const districts = getAllSchoolDistricts(indexes);
 * // ['BASIS Independent Schools', 'Challenger Schools', ...]
 */
export function getAllSchoolDistricts(indexes: QuestIndexes): string[] {
  return Array.from(indexes.districtGradeIndex.keys()).sort();
}

/**
 * 获取所有可用的年级列表
 *
 * @param indexes - Quest 索引
 * @returns 年级列表（按字母顺序排序）
 *
 * @example
 * const grades = getAllGrades(indexes);
 * // ['Grade 1', 'Grade 2', 'Grade 3', ...]
 */
export function getAllGrades(indexes: QuestIndexes): string[] {
  return Array.from(indexes.gradeIndex.keys()).sort();
}

/**
 * 获取指定学区支持的年级列表
 *
 * @param indexes - Quest 索引
 * @param schoolDistrict - 学区名称
 * @returns 年级列表（按字母顺序排序）
 *
 * @example
 * const grades = getGradesByDistrict(indexes, 'Challenger Schools');
 * // ['Grade 3', 'Grade 5', ...]
 */
export function getGradesByDistrict(
  indexes: QuestIndexes,
  schoolDistrict: string
): string[] {
  const gradeMap = indexes.districtGradeIndex.get(schoolDistrict);
  if (!gradeMap) return [];
  return Array.from(gradeMap.keys()).sort();
}

/**
 * 格式化学区-年级配置为显示字符串
 *
 * @param quest - Quest 对象
 * @param options - 格式化选项
 * @param options.shortNames - 是否使用缩写学校名（默认 true）
 * @param options.maxLength - 最大字符串长度（默认 100）
 * @returns 格式化的字符串
 *
 * @example
 * formatSchoolGradeDisplay(quest)
 * // 输出: "Challenger (3), BASIS Ind. (5-8)"
 *
 * @example
 * formatSchoolGradeDisplay(quest, { shortNames: false })
 * // 输出: "Challenger Schools (Grade 3), BASIS Independent Schools (Grade 5-8)"
 */
export function formatSchoolGradeDisplay(
  quest: Quest,
  options: {
    shortNames?: boolean;
    maxLength?: number;
  } = {}
): string {
  const { shortNames = true, maxLength = 100 } = options;
  const defaultGrade = quest.default_grade;
  const publicDistricts = quest.public_school_districts || [];
  const specialMappings = quest.school_district_grade_mapping || [];

  if (!defaultGrade && publicDistricts.length === 0 && specialMappings.length === 0) {
    return 'No district/grade configuration';
  }

  const parts: string[] = [];

  // 处理 public_school_districts + default_grade
  if (defaultGrade && publicDistricts.length > 0) {
    publicDistricts.forEach(district => {
      let displayName = district;
      if (shortNames) {
        displayName = district
          .replace(' Schools', '')
          .replace(' Independent', ' Ind.')
          .replace(' Charter', ' Ch.')
          .replace(' Unified SD', ' USD')
          .replace(' Union SD', ' USD')
          .trim();
      }

      // 提取年级数字
      const gradeNum = defaultGrade.match(/\d+/)?.[0] || defaultGrade;
      parts.push(`${displayName} (${gradeNum})`);
    });
  }

  // 处理 special mappings
  specialMappings.forEach(mapping => {
    let displayName = mapping.school_district;
    if (shortNames) {
      displayName = displayName
        .replace(' Schools', '')
        .replace(' Independent', ' Ind.')
        .replace(' Charter', ' Ch.')
        .replace(' Unified SD', ' USD')
        .replace(' Union SD', ' USD')
        .trim();
    }

    const grades = mapping.grades && mapping.grades.length > 0
      ? mapping.grades
      : (defaultGrade ? [defaultGrade] : []);

    // 提取年级数字
    const gradeNums = grades
      .map(g => {
        const match = g.match(/\d+/);
        return match ? parseInt(match[0]) : null;
      })
      .filter((n): n is number => n !== null)
      .sort((a, b) => a - b);

    let gradeStr = 'All';
    if (gradeNums.length > 0) {
      // 检查是否连续
      const isConsecutive = gradeNums.every((num, i) =>
        i === 0 || num === gradeNums[i - 1] + 1
      );

      if (isConsecutive && gradeNums.length > 1) {
        gradeStr = `${gradeNums[0]}-${gradeNums[gradeNums.length - 1]}`;
      } else {
        gradeStr = gradeNums.join(',');
      }
    }

    parts.push(`${displayName} (${gradeStr})`);
  });

  let result = parts.join(', ');

  // 截断过长的字符串
  if (result.length > maxLength) {
    result = result.substring(0, maxLength - 3) + '...';
  }

  return result;
}

/**
 * 获取 Quest 的所有年级（用于显示和向后兼容）
 *
 * @param quest - Quest 对象
 * @returns 年级列表（去重并排序）
 *
 * @example
 * const grades = getQuestGrades(quest);
 * // ['Grade 3', 'Grade 5', 'Grade 7']
 */
export function getQuestGrades(quest: Quest): string[] {
  const gradesSet = new Set<string>();

  // 添加 default_grade
  if (quest.default_grade) {
    gradesSet.add(quest.default_grade);
  }

  // 添加 special mappings 中的年级
  const specialMappings = quest.school_district_grade_mapping || [];
  specialMappings.forEach(mapping => {
    if (mapping.grades && mapping.grades.length > 0) {
      mapping.grades.forEach(g => gradesSet.add(g));
    } else if (quest.default_grade) {
      // 如果 mapping 没有指定年级，使用 default_grade
      gradesSet.add(quest.default_grade);
    }
  });

  return Array.from(gradesSet).sort();
}

/**
 * 检查单个 Quest 是否匹配学区和年级筛选条件（用于简单筛选场景）
 *
 * @param quest - Quest 对象
 * @param schoolDistrict - 学区名称，'all' 表示不筛选学区
 * @param grade - 年级，'all' 表示不筛选年级
 * @returns 是否匹配
 *
 * @example
 * // 只筛选年级
 * const matches = matchQuestByDistrictAndGrade(quest, 'all', 'Grade 3');
 *
 * @example
 * // 筛选学区和年级
 * const matches = matchQuestByDistrictAndGrade(quest, 'Challenger Schools', 'Grade 3');
 */
export function matchQuestByDistrictAndGrade(
  quest: Quest,
  schoolDistrict: string = 'all',
  grade: string = 'all'
): boolean {
  const defaultGrade = quest.default_grade;
  const publicDistricts = quest.public_school_districts || [];
  const specialMappings = quest.school_district_grade_mapping || [];

  // 没有任何筛选条件
  if ((schoolDistrict === 'all' || !schoolDistrict) && (grade === 'all' || !grade)) {
    return true;
  }

  // === 场景1: 用户选择了学区 ===
  if (schoolDistrict && schoolDistrict !== 'all') {
    // 检查学区是否在 public_school_districts 中
    if (publicDistricts.includes(schoolDistrict)) {
      // 使用 default_grade 匹配
      if (grade && grade !== 'all') {
        return defaultGrade === grade;
      }
      return true;  // 学区匹配，没有年级筛选
    }

    // 检查学区是否在 school_district_grade_mapping 中
    const specialMapping = specialMappings.find(
      m => m.school_district === schoolDistrict
    );
    if (specialMapping) {
      if (grade && grade !== 'all') {
        const effectiveGrades = (specialMapping.grades && specialMapping.grades.length > 0)
          ? specialMapping.grades
          : (defaultGrade ? [defaultGrade] : []);
        return effectiveGrades.includes(grade);
      }
      return true;  // 学区匹配，没有年级筛选
    }

    // 学区不在配置中
    return false;
  }

  // === 场景2: 用户没有选择学区 ===
  // 直接匹配年级
  if (grade && grade !== 'all') {
    const grades = getQuestGrades(quest);
    return grades.includes(grade);
  }

  return true;
}

/**
 * 获取 Quest 的所有学区（用于显示和匹配）
 *
 * @param quest - Quest 对象
 * @returns 学区列表（去重并排序）
 *
 * @example
 * const districts = getQuestDistricts(quest);
 * // ['Challenger Schools', 'BASIS Independent Schools']
 */
export function getQuestDistricts(quest: Quest): string[] {
  const districtsSet = new Set<string>();

  // 添加 public_school_districts
  const publicDistricts = quest.public_school_districts || [];
  publicDistricts.forEach(d => districtsSet.add(d));

  // 添加 special mappings 中的学区
  const specialMappings = quest.school_district_grade_mapping || [];
  specialMappings.forEach(mapping => {
    districtsSet.add(mapping.school_district);
  });

  return Array.from(districtsSet).sort();
}
